import { DEFAULT_LANG, resolveLang, type Lang, type Me } from '@fonus/shared';
import { t, type MessageKey } from '@fonus/shared/i18n';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api } from '../api/client.ts';
import { keys } from '../api/queries.ts';

/** Before /me arrives: Telegram language_code from the launch data, else Uzbek. */
function launchLang(): Lang {
  try {
    const raw = new URLSearchParams(window.location.hash.slice(1)).get('tgWebAppData');
    const user = raw
      ? (JSON.parse(new URLSearchParams(raw).get('user') ?? '{}') as { language_code?: string })
      : {};
    return resolveLang(user.language_code);
  } catch {
    return DEFAULT_LANG;
  }
}

const fallbackLang = launchLang();

/** Current UI language: the profile's language (optimistically updated on switch). */
export function useLang(): Lang {
  // Subscribes to the cached profile; fetching is owned by useMe() in App.
  const { data } = useQuery<Me>({
    queryKey: keys.me,
    queryFn: () => api<Me>('/me'),
    enabled: false,
  });
  return data?.lang ?? fallbackLang;
}

export type T = (key: MessageKey, params?: Record<string, string | number>) => string;

export function useT(): T {
  const lang = useLang();
  return useCallback<T>((key, params) => t(lang, key, params), [lang]);
}

/** Non-hook access for stores (audio engine, toasts). */
export function useLangGetter(): () => Lang {
  const qc = useQueryClient();
  return () => qc.getQueryData<Me>(keys.me)?.lang ?? fallbackLang;
}
